<template lang="pug">
.setup-page
  .page-header
    .page-title Setup
    .page-sub Configure your client, manage packages and assets

  .tab-row
    button.tab-btn(
      v-for="tab in tabs"
      :key="tab.key"
      :class="{ active: activeTab === tab.key }"
      @click="setTab(tab.key)"
    ) {{ tab.label }}

  template(v-if="activeTab === 'autoexec'")
    .editor-toolbar
      span.editor-filename id1 /&nbsp;
        span autoexec.cfg
      .editor-actions
        button.btn-ghost-sm(@click="loadRecommended('autoexec')") Load Recommended
        button.btn-save(@click="save('autoexec')") Save
    textarea.cfg-editor(
      spellcheck="false"
      :value="gameStore.autoexecFile"
      @input="onAutoexecInput"
      @keydown.ctrl.s.prevent="save('autoexec')"
    )
    .editor-footer
      span.editor-hint Ctrl+S to save &nbsp;·&nbsp; runs after config.cfg on every startup
      span.editor-status(v-if="savedFor === 'autoexec'") Saved

  template(v-if="activeTab === 'config'")
    .editor-toolbar
      span.editor-filename id1 /&nbsp;
        span config.cfg
      .editor-actions
        button.btn-ghost-sm(@click="loadRecommended('config')") Load Recommended
        button.btn-save(@click="save('config')") Save
    textarea.cfg-editor(
      spellcheck="false"
      :value="gameStore.configFile"
      @input="onConfigInput"
      @keydown.ctrl.s.prevent="save('config')"
    )
    .editor-footer
      span.editor-hint Ctrl+S to save &nbsp;·&nbsp; auto-generated by engine on exit
      span.editor-status(v-if="savedFor === 'config'") Saved

  template(v-if="activeTab === 'packages'")
    ID1Assets
    Packages
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGameStore } from '../../../stores/game'
import ID1Assets from './SetupGame/ID1Assets.vue'
import Packages from './SetupGame/Assets/Packages.vue'

const gameStore = useGameStore()
const route = useRoute()
const router = useRouter()

const tabs = [
  { key: 'autoexec', label: 'autoexec.cfg' },
  { key: 'config', label: 'config.cfg' },
  { key: 'packages', label: 'Packages' },
]

const validTabs = tabs.map(t => t.key)
const initialTab = validTabs.includes(route.query.tab as string) ? route.query.tab as string : 'autoexec'
const activeTab = ref(initialTab)

const setTab = (key: string) => {
  activeTab.value = key
  router.replace({ query: { ...route.query, tab: key } })
}
const savedFor = ref('')
let savedTimer: ReturnType<typeof setTimeout> | null = null

const save = (tab: 'autoexec' | 'config') => {
  savedFor.value = tab
  if (savedTimer) clearTimeout(savedTimer)
  savedTimer = setTimeout(() => { savedFor.value = '' }, 2500)
}

const onAutoexecInput = (e: Event) => {
  gameStore.saveAutoexec((e.target as HTMLTextAreaElement).value)
}

const onConfigInput = (e: Event) => {
  gameStore.saveConfig((e.target as HTMLTextAreaElement).value)
}

const loadRecommended = (tab: 'autoexec' | 'config') => {
  const which = tab === 'autoexec' ? 'autoexec.cfg' : 'config.cfg'
  if (!confirm(`Replace current ${which} with recommended settings?`)) return
  if (tab === 'autoexec') {
    gameStore.loadRecommendedAutoexec()
  } else {
    gameStore.loadRecommendedConfig()
  }
}
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.setup-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: $gap-12 $gap-12;
}

.page-header { margin-bottom: 40px; }

.page-title {
  font-size: 28px;
  font-weight: $fw-black;
  color: $palette-bright;
  letter-spacing: -0.02em;
}

.page-sub {
  font-size: $font-sm;
  color: $palette-muted;
  margin-top: 4px;
}

/* ── Tabs ── */
.tab-row {
  display: flex;
  border-bottom: $border-subtle;
  margin-bottom: 32px;
  overflow-x: auto;
  overflow-y: hidden;
}

.tab-btn {
  font-family: inherit;
  font-size: $font-sm;
  font-weight: $fw-bold;
  text-transform: uppercase;
  letter-spacing: $tracking-links;
  padding: 12px 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: $palette-muted;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: $transition-color;
  white-space: nowrap;
  &:hover { color: $palette-text; }
  &.active { color: $palette-bright; border-bottom-color: $palette-red; }
}

/* ── Editor ── */
.editor-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: $palette-surface;
  border: $border-subtle;
  border-bottom: none;
}

.editor-filename {
  font-size: $font-xs;
  font-weight: $fw-bold;
  color: $palette-muted;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-family: 'JetBrains Mono', monospace;
  span { color: $palette-text; }
}

.editor-actions { display: flex; gap: 8px; }

.btn-ghost-sm {
  font-family: inherit;
  font-size: $font-xs;
  font-weight: $fw-bold;
  letter-spacing: $tracking-links;
  text-transform: uppercase;
  padding: 7px 16px;
  background: transparent;
  border: $border-subtle;
  color: $palette-muted;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: $palette-bright; border-color: $palette-text; }
}

.btn-save {
  font-family: inherit;
  font-size: $font-xs;
  font-weight: $fw-bold;
  letter-spacing: $tracking-links;
  text-transform: uppercase;
  padding: 7px 16px;
  background: $palette-red;
  color: $palette-bright;
  border: none;
  cursor: pointer;
  transition: $transition-bg;
  &:hover { background: lighten($palette-red, 6%); }
}

.cfg-editor {
  display: block;
  width: 100%;
  min-height: 420px;
  background: #111111;
  border: $border-subtle;
  color: #c8c8c8;
  font-family: 'JetBrains Mono', monospace;
  font-size: $font-sm;
  line-height: 1.7;
  padding: 20px;
  resize: vertical;
  outline: none;
  caret-color: $palette-red;
  &:focus { border-color: $palette-muted; }
}

.editor-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: $palette-surface;
  border: $border-subtle;
  border-top: none;
  margin-bottom: 40px;
}

.editor-hint {
  font-size: $font-2xs;
  color: $palette-muted;
  font-family: 'JetBrains Mono', monospace;
}

.editor-status {
  font-size: $font-2xs;
  color: #4a9e4a;
  font-family: 'JetBrains Mono', monospace;
}

@media (max-width: 768px) {
  .setup-page { padding: 32px 24px; }
  .tab-btn { padding: 12px 16px; }
}

@media (max-width: 480px) {
  .setup-page { padding: 24px 16px; }
  .editor-toolbar { flex-direction: column; align-items: flex-start; gap: 10px; }
}
</style>
